import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import DashboardLayout from './DashboardLayout';
import { customMonitoringApi } from '../lib/api';
import { useSuccessAlert, useErrorAlert } from './AlertSystem';
import { extractErrorMessage } from '../lib/utils';
import './DashboardCustomMonitoring.css';

interface MonitoringRequest {
  name: string;
  email: string;
  company: string;
  serviceType: string;
  message: string;
  budgetRange: string;
}

interface UserRequest {
  id: number;
  name: string;
  email: string;
  company: string;
  monitoringType: string;
  description: string;
  budgetRange: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const DashboardCustomMonitoring: React.FC = () => {
  const [activeView, setActiveView] = useState<'submit' | 'requests'>('submit');
  
  // Form state
  const [formData, setFormData] = useState<MonitoringRequest>({
    name: '',
    email: '',
    company: '',
    serviceType: '',
    message: '',
    budgetRange: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Requests viewing state
  const [allRequests, setAllRequests] = useState<UserRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState<UserRequest | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const requestsPerPage = 10;

  // Alert hooks
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();

  // Custom react-select styles (matching alerts page)
  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      background: 'rgba(26, 26, 39, 0.8)',
      border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
      borderRadius: '8px',
      padding: '2px 4px',
      color: 'var(--text-primary)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif",
      minHeight: '40px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
      cursor: 'pointer',
      '&:hover': {
        border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
      }
    }),
    menu: (provided: any) => ({
      ...provided,
      background: 'rgba(26, 26, 39, 0.95)',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      zIndex: 99999999,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)'
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      background: state.isSelected 
        ? 'rgba(99, 102, 241, 0.2)' 
        : state.isFocused 
          ? 'rgba(255, 255, 255, 0.08)' 
          : 'transparent',
      color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
      padding: '10px 14px',
      cursor: 'pointer',
      fontFamily: "'Inter', 'Chivo', sans-serif",
      fontSize: '0.875rem',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.08)'
      }
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--text-primary)',
      fontFamily: "'Inter', 'Chivo', sans-serif"
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'rgba(148, 163, 184, 0.7)',
      fontFamily: "'Inter', 'Chivo', sans-serif"
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--text-primary)',
      fontFamily: "'Inter', 'Chivo', sans-serif"
    })
  };

  const serviceTypeOptions = [
    { value: 'Network Monitoring', label: 'Network Monitoring' },
    { value: 'Endpoint Detection & Response', label: 'Endpoint Detection & Response' },
    { value: 'Cloud Security Monitoring', label: 'Cloud Security Monitoring' },
    { value: 'Web Application Security', label: 'Web Application Security' },
    { value: 'Database Monitoring', label: 'Database Monitoring' },
    { value: 'Email Security', label: 'Email Security' },
    { value: 'Mobile Device Management', label: 'Mobile Device Management' },
    { value: 'IoT Security', label: 'IoT Security' },
    { value: 'Industrial Control Systems', label: 'Industrial Control Systems' },
    { value: 'Comprehensive Security Package', label: 'Comprehensive Security Package' },
    { value: 'Other', label: 'Other (specify in message)' }
  ];

  const budgetRangeOptions = [
    { value: 'Under $10,000', label: 'Under $10,000' },
    { value: '$10,000 - $50,000', label: '$10,000 - $50,000' },
    { value: '$50,000 - $100,000', label: '$50,000 - $100,000' },
    { value: '$100,000 - $500,000', label: '$100,000 - $500,000' },
    { value: 'Over $500,000', label: 'Over $500,000' },
    { value: 'Enterprise Pricing', label: 'Enterprise Pricing' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'NEW', label: 'New' },
    { value: 'REVIEWING', label: 'Under Review' },
    { value: 'QUOTED', label: 'Quoted' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'REJECTED', label: 'Rejected' }
  ];

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter and pagination logic
  const filteredRequests = useMemo(() => {
    return allRequests.filter(request => {
      const matchesSearch = !debouncedSearchQuery || 
        request.company.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        request.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        request.monitoringType.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        request.description.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [allRequests, debouncedSearchQuery, statusFilter]);

  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * requestsPerPage,
    currentPage * requestsPerPage
  );

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, statusFilter]);

  // Initial load of user's requests for stats
  useEffect(() => {
    const fetchInitialRequests = async () => {
      try {
        const response = await customMonitoringApi.getMyRequests({ limit: 1000 });

        if (response.success && response.data) {
          setAllRequests(response.data.requests || []);
        }
      } catch (error) {
        console.error('Error fetching initial requests:', error);
        // Don't set error state for initial load, just log it
      }
    };

    fetchInitialRequests();
  }, []); // Run once on component mount

  // Fetch user's requests
  useEffect(() => {
    if (activeView === 'requests') {
      const fetchRequests = async () => {
        try {
          setLoading(true);
          setError(null);

          const response = await customMonitoringApi.getMyRequests({ limit: 1000 });

          if (response.success && response.data) {
            setAllRequests(response.data.requests || []);
          } else {
            throw new Error(response.error || 'Failed to fetch requests');
          }
        } catch (error) {
          console.error('Error fetching requests:', error);
          setError(error instanceof Error ? error.message : 'Failed to fetch requests');
          setAllRequests([]);
        } finally {
          setLoading(false);
        }
      };

      fetchRequests();
    }
  }, [activeView]);

  const handleInputChange = (field: keyof MonitoringRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      showError('Name is required', 'Validation Error');
      return false;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      showError('Valid email is required', 'Validation Error');
      return false;
    }
    if (!formData.company.trim()) {
      showError('Company name is required', 'Validation Error');
      return false;
    }
    if (!formData.serviceType) {
      showError('Please select a service type', 'Validation Error');
      return false;
    }
    if (!formData.message.trim()) {
      showError('Please describe your monitoring requirements', 'Validation Error');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const requestData = {
        businessInfo: {
          companyName: formData.company,
          industry: 'Not specified',
          contactPerson: formData.name,
          email: formData.email
        },
        requirements: {
          monitoringType: [formData.serviceType],
          budget: formData.budgetRange || 'Not specified',
          timeline: 'To be discussed'
        },
        technicalDetails: {
          currentSecurity: formData.message
        },
        additionalInfo: {
          description: formData.message
        }
      };

      const response = await customMonitoringApi.submitRequest(requestData);
      
      if (response.success) {
        setSubmitted(true);
        showSuccess('Request submitted successfully! We will contact you soon.', 'Success');
        // Refresh requests if we're viewing them
        if (activeView === 'requests') {
          const refreshResponse = await customMonitoringApi.getMyRequests({ limit: 1000 });
          if (refreshResponse.success && refreshResponse.data) {
            setAllRequests(refreshResponse.data.requests || []);
          }
        }
      } else {
        const errorMsg = extractErrorMessage(response);
        showError(errorMsg, 'Submission Failed');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      showError('Network error occurred. Please try again.', 'Connection Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      serviceType: '',
      message: '',
      budgetRange: ''
    });
    setSubmitted(false);
    setActiveView('submit');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getTimeAgo = (dateString: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return formatDate(dateString);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'NEW': return '#3b82f6';
      case 'REVIEWING': return '#f59e0b';
      case 'QUOTED': return '#8b5cf6';
      case 'APPROVED': return '#22c55e';
      case 'COMPLETED': return '#10b981';
      case 'REJECTED': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const handleRequestClick = (request: UserRequest) => {
    setSelectedRequest(request);
    setIsDetailModalOpen(true);
  };

  const closeModal = () => {
    setSelectedRequest(null);
    setIsDetailModalOpen(false);
  };

  if (submitted) {
    return (
      <DashboardLayout title="Custom Monitoring">
        <div className="dashboard-monitoring-success">
          <div className="dashboard-monitoring-success-icon">
            <i className="ri-check-line"></i>
          </div>
          <h1>Request Submitted Successfully!</h1>
          <p>
            Thank you for your custom monitoring request. Our security experts will review your requirements 
            and get back to you within 1-2 business days with a personalized solution.
          </p>
          <div className="dashboard-monitoring-success-details">
            <div className="dashboard-monitoring-success-detail-item">
              <i className="ri-mail-line"></i>
              <span>Confirmation email sent to {formData.email}</span>
            </div>
            <div className="dashboard-monitoring-success-detail-item">
              <i className="ri-time-line"></i>
              <span>Expected response within 1-2 business days</span>
            </div>
            <div className="dashboard-monitoring-success-detail-item">
              <i className="ri-phone-line"></i>
              <span>Emergency contact: support@nuroveli.com</span>
            </div>
          </div>
          <div className="dashboard-monitoring-success-actions">
            <button 
              className="dashboard-monitoring-success-btn primary" 
              onClick={() => {
                resetForm();
                setActiveView('submit');
              }}
            >
              Submit Another Request
            </button>
            <button 
              className="dashboard-monitoring-success-btn secondary" 
              onClick={() => {
                setSubmitted(false);
                setActiveView('requests');
              }}
            >
              View My Requests
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading && activeView === 'requests') {
    return (
      <DashboardLayout title="Custom Monitoring">
        <div className="dashboard-monitoring-loading">
          <div className="dashboard-monitoring-loading-spinner">
            <i className="ri-loader-4-line"></i>
          </div>
          <div className="dashboard-monitoring-loading-text">Loading requests...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (error && activeView === 'requests') {
    return (
      <DashboardLayout title="Custom Monitoring">
        <div className="dashboard-monitoring-error">
          <div className="dashboard-monitoring-error-icon">
            <i className="ri-error-warning-line"></i>
          </div>
          <div className="dashboard-monitoring-error-content">
            <h3>Failed to Load Requests</h3>
            <p>{error}</p>
            <button
              className="dashboard-monitoring-retry-btn"
              onClick={() => window.location.reload()}
            >
              <i className="ri-refresh-line"></i>
              Try Again
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Custom Monitoring">
      <div className="dashboard-monitoring">
        {/* Header */}
        <div className="dashboard-monitoring-header">
          <div className="dashboard-monitoring-header-content">
            <h1 className="dashboard-monitoring-title">Custom Monitoring Solutions</h1>
            <p className="dashboard-monitoring-subtitle">
              Request tailored security monitoring solutions and track your submissions
            </p>
          </div>
          <div className="dashboard-monitoring-stats">
            <div className="dashboard-monitoring-stat">
              <div className="dashboard-monitoring-stat-value">{allRequests.length}</div>
              <div className="dashboard-monitoring-stat-label">Total</div>
            </div>
            <div className="dashboard-monitoring-stat">
              <div className="dashboard-monitoring-stat-value">
                {allRequests.filter(r => r.status === 'NEW').length}
              </div>
              <div className="dashboard-monitoring-stat-label">New</div>
            </div>
            <div className="dashboard-monitoring-stat">
              <div className="dashboard-monitoring-stat-value">
                {allRequests.filter(r => r.status === 'COMPLETED').length}
              </div>
              <div className="dashboard-monitoring-stat-label">Completed</div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="dashboard-monitoring-tabs">
          <button 
            className={`dashboard-monitoring-tab ${activeView === 'submit' ? 'active' : ''}`}
            onClick={() => setActiveView('submit')}
          >
            <i className="ri-add-line"></i>
            Submit Request
          </button>
          <button 
            className={`dashboard-monitoring-tab ${activeView === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveView('requests')}
          >
            <i className="ri-file-list-3-line"></i>
            My Requests ({allRequests.length})
          </button>
        </div>

        {/* Content Area */}
        <div className="dashboard-monitoring-content">
          {activeView === 'submit' && (
            <div className="dashboard-monitoring-form">
              <div className="dashboard-monitoring-form-container">
                <div className="dashboard-monitoring-form-section">
                  <h2 className="dashboard-monitoring-section-title">Contact Information</h2>
                  
                  <div className="dashboard-monitoring-form-row">
                    <div className="dashboard-monitoring-form-group">
                      <label htmlFor="name" className="dashboard-monitoring-form-label">Contact Person *</label>
                      <input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter your full name"
                        className="dashboard-monitoring-form-input"
                      />
                    </div>

                    <div className="dashboard-monitoring-form-group">
                      <label htmlFor="email" className="dashboard-monitoring-form-label">Email Address *</label>
                      <input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter your business email"
                        className="dashboard-monitoring-form-input"
                      />
                    </div>
                  </div>

                  <div className="dashboard-monitoring-form-group">
                    <label htmlFor="company" className="dashboard-monitoring-form-label">Company Name *</label>
                    <input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      placeholder="Enter your company name"
                      className="dashboard-monitoring-form-input"
                    />
                  </div>
                </div>

                <div className="dashboard-monitoring-form-section">
                  <h2 className="dashboard-monitoring-section-title">Service Requirements</h2>
                  
                  <div className="dashboard-monitoring-form-row">
                    <div className="dashboard-monitoring-form-group">
                      <label htmlFor="serviceType" className="dashboard-monitoring-form-label">Service Type *</label>
                      <Select
                        inputId="serviceType"
                        value={serviceTypeOptions.find((option: any) => option.value === formData.serviceType) || null}
                        onChange={(option: any) => handleInputChange('serviceType', option?.value || '')}
                        options={serviceTypeOptions}
                        styles={selectStyles}
                        placeholder="Select monitoring service..."
                        isClearable
                        className="dashboard-monitoring-select"
                      />
                    </div>

                    <div className="dashboard-monitoring-form-group">
                      <label htmlFor="budgetRange" className="dashboard-monitoring-form-label">Budget Range (Optional)</label>
                      <Select
                        inputId="budgetRange"
                        value={budgetRangeOptions.find((option: any) => option.value === formData.budgetRange) || null}
                        onChange={(option: any) => handleInputChange('budgetRange', option?.value || '')}
                        options={budgetRangeOptions}
                        styles={selectStyles}
                        placeholder="Select budget range..."
                        isClearable
                        className="dashboard-monitoring-select"
                      />
                    </div>
                  </div>

                  <div className="dashboard-monitoring-form-group">
                    <label htmlFor="message" className="dashboard-monitoring-form-label">Requirements Description *</label>
                    <textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Describe your specific monitoring requirements, current security setup, and any special needs..."
                      className="dashboard-monitoring-form-textarea"
                      rows={5}
                    />
                  </div>
                </div>

                <div className="dashboard-monitoring-form-actions">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="dashboard-monitoring-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeView === 'requests' && (
            <div className="dashboard-monitoring-requests">
              {/* Filters */}
              <div className="dashboard-monitoring-filters">
                <div className="dashboard-monitoring-filters-row">
                  <div className="dashboard-monitoring-search-group">
                    <div className="dashboard-monitoring-search-input-wrapper">
                      <i className="ri-search-line"></i>
                      <input
                        type="text"
                        placeholder="Search requests..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="dashboard-monitoring-search-input"
                      />
                    </div>
                  </div>
                  
                  <div className="dashboard-monitoring-filter-group">
                    <Select
                      value={statusOptions.find((option: any) => option.value === statusFilter) || statusOptions[0]}
                      onChange={(option: any) => setStatusFilter(option?.value || 'all')}
                      options={statusOptions}
                      styles={selectStyles}
                      isSearchable={false}
                      className="dashboard-monitoring-status-filter"
                    />
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="dashboard-monitoring-results-summary">
                <span className="dashboard-monitoring-results-count">
                  {filteredRequests.length} requests found
                </span>
                {debouncedSearchQuery && (
                  <span className="dashboard-monitoring-search-term">
                    for "{debouncedSearchQuery}"
                  </span>
                )}
              </div>

              {/* Requests List */}
              {filteredRequests.length === 0 ? (
                <div className="dashboard-monitoring-empty-state">
                  <div className="dashboard-monitoring-empty-icon">
                    <i className="ri-file-list-3-line"></i>
                  </div>
                  <h3>No requests found</h3>
                  <p>
                    {debouncedSearchQuery || statusFilter !== 'all'
                      ? 'Try adjusting your search or filter criteria.'
                      : "You haven't submitted any custom monitoring requests yet."
                    }
                  </p>
                  {!debouncedSearchQuery && statusFilter === 'all' && (
                    <button 
                      className="dashboard-monitoring-empty-action-btn"
                      onClick={() => setActiveView('submit')}
                    >
                      Submit Your First Request
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="dashboard-monitoring-cards">
                    {paginatedRequests.map((request) => (
                      <div 
                        key={request.id} 
                        className="dashboard-monitoring-card"
                        onClick={() => handleRequestClick(request)}
                      >
                        <div className="dashboard-monitoring-card-header">
                          <div className="dashboard-monitoring-card-title">
                            <h3>{request.company}</h3>
                            <span 
                              className="dashboard-monitoring-status-badge"
                              style={{ 
                                backgroundColor: `${getStatusColor(request.status)}20`,
                                color: getStatusColor(request.status),
                                border: `1px solid ${getStatusColor(request.status)}40`
                              }}
                            >
                              {request.status}
                            </span>
                          </div>
                          <div className="dashboard-monitoring-card-meta">
                            <span className="dashboard-monitoring-card-time">
                              {getTimeAgo(request.createdAt)}
                            </span>
                          </div>
                        </div>

                        <div className="dashboard-monitoring-card-content">
                          <div className="dashboard-monitoring-card-info">
                            <div className="dashboard-monitoring-card-info-item">
                              <span className="dashboard-monitoring-card-info-label">Service:</span>
                              <span className="dashboard-monitoring-card-info-value">{request.monitoringType}</span>
                            </div>
                            <div className="dashboard-monitoring-card-info-item">
                              <span className="dashboard-monitoring-card-info-label">Budget:</span>
                              <span className="dashboard-monitoring-card-info-value">{request.budgetRange}</span>
                            </div>
                          </div>
                          <div className="dashboard-monitoring-card-description">
                            <p>{request.description.length > 150 ? `${request.description.substring(0, 150)}...` : request.description}</p>
                          </div>
                        </div>

                        <div className="dashboard-monitoring-card-footer">
                          <div className="dashboard-monitoring-card-footer-info">
                            <span>ID: #{request.id}</span>
                          </div>
                          <div className="dashboard-monitoring-card-actions">
                            <button className="dashboard-monitoring-card-action-btn">
                              <i className="ri-eye-line"></i>
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="dashboard-monitoring-pagination">
                      <button 
                        className="dashboard-monitoring-pagination-btn"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        <i className="ri-arrow-left-line"></i>
                        Previous
                      </button>
                      
                      <div className="dashboard-monitoring-pagination-info">
                        <span>Page {currentPage} of {totalPages}</span>
                      </div>
                      
                      <button 
                        className="dashboard-monitoring-pagination-btn"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                        <i className="ri-arrow-right-line"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {isDetailModalOpen && selectedRequest && (
          <div className="dashboard-monitoring-modal-overlay" onClick={closeModal}>
            <div className="dashboard-monitoring-modal" onClick={(e) => e.stopPropagation()}>
              <div className="dashboard-monitoring-modal-header">
                <h2>Request Details</h2>
                <button className="dashboard-monitoring-modal-close" onClick={closeModal}>
                  <i className="ri-close-line"></i>
                </button>
              </div>
              
              <div className="dashboard-monitoring-modal-content">
                <div className="dashboard-monitoring-modal-section">
                  <h3>Request Information</h3>
                  <div className="dashboard-monitoring-modal-grid">
                    <div className="dashboard-monitoring-modal-field">
                      <label>Request ID:</label>
                      <span>#{selectedRequest.id}</span>
                    </div>
                    <div className="dashboard-monitoring-modal-field">
                      <label>Status:</label>
                      <span 
                        className="dashboard-monitoring-status-badge"
                        style={{ 
                          backgroundColor: `${getStatusColor(selectedRequest.status)}20`,
                          color: getStatusColor(selectedRequest.status),
                          border: `1px solid ${getStatusColor(selectedRequest.status)}40`
                        }}
                      >
                        {selectedRequest.status}
                      </span>
                    </div>
                    <div className="dashboard-monitoring-modal-field">
                      <label>Company:</label>
                      <span>{selectedRequest.company}</span>
                    </div>
                    <div className="dashboard-monitoring-modal-field">
                      <label>Service Type:</label>
                      <span>{selectedRequest.monitoringType}</span>
                    </div>
                    <div className="dashboard-monitoring-modal-field">
                      <label>Budget Range:</label>
                      <span>{selectedRequest.budgetRange}</span>
                    </div>
                    <div className="dashboard-monitoring-modal-field">
                      <label>Submitted:</label>
                      <span>{formatDate(selectedRequest.createdAt)}</span>
                    </div>
                    <div className="dashboard-monitoring-modal-field">
                      <label>Last Updated:</label>
                      <span>{formatDate(selectedRequest.updatedAt)}</span>
                    </div>
                  </div>
                </div>

                <div className="dashboard-monitoring-modal-section">
                  <h3>Requirements Description</h3>
                  <div className="dashboard-monitoring-modal-description">
                    <p>{selectedRequest.description}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardCustomMonitoring;
